// ══════════════════════════════════════════════════════════════════════════════════════════════
// CE QU'UN CANAL DE CAMPAGNE S'APPELLE À L'ÉCRAN — une seule table, pour les trois surfaces.
//
// POURQUOI CE MODULE EXISTE. Le nom du canal était composé par TERNAIRE dans trois écrans :
// `c.channel === 'sms' ? 'SMS' : t('E-mail')` au tableau de bord, la même chose en minuscules dans la
// liste des campagnes, une troisième dans l'assistant. Ajouter un canal ne cassait donc RIEN — le
// wallet se serait affiché « E-mail » partout, avec trois portes vertes (**Forme EA** du catalogue).
//
// ⚠️ `Record<CampaignChannel, …>` OBLIGE le compilateur à réclamer le canal suivant. C'est la seule
// forme qui tienne : une table partielle, ou un `switch` sans cas par défaut refusé, se satisferait
// du silence.
//
// ⚠️ DES FONCTIONS, PAS DES VALEURS. `t()` résout dans la langue du COMPTE, connue seulement après le
// chargement des modules : une table de chaînes figerait la langue de la plateforme au démarrage, et
// aucune erreur ne le dirait.
//
// ⚠️ LE MOT DU SERVEUR NE SE RENOMME PAS. `wallet_push` voyage dans le corps de requête et s'écrit en
// base ; ce module le TRADUIT pour l'affichage, il ne lui substitue jamais un autre identifiant.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { t } from '@orgazone/i18n';
import type { CampaignChannel } from './api';

/** Le nom du canal, tel qu'un exploitant le lit. */
const LABEL: Record<CampaignChannel, () => string> = {
  // ⚠️ « SMS » RESTE NU : c'est un sigle de cinq caractères, que le relevé des codes courts
  //    reconnaît et adjuge comme tel — il s'écrit pareil dans les trois langues.
  // ⚠️ « Wallet » PASSE PAR `t()`, AVEC TROIS FORMES IDENTIQUES, et ce n'est pas une contradiction :
  //    six caractères sortent de l'exemption des codes courts, donc le taire créerait précisément le
  //    trou que le compteur ferme. Trois formes identiques sont une réponse VALABLE à condition
  //    d'être ÉCRITE (Forme DF) — et si un jour une langue le traduit, la ligne existe déjà.
  sms: () => 'SMS',
  email: () => t('E-mail'),
  wallet_push: () => t('Wallet'),
};

export const channelLabel = (c: CampaignChannel): string => LABEL[c]();

/**
 * CE QUI REND UNE PERSONNE JOIGNABLE SUR CE CANAL — la phrase que l'écran affiche sous l'audience.
 *
 * ⚠️ TROIS PHRASES ENTIÈRES, pas une phrase à trou. « Adresse renseignée, consentement {canal} donné
 * à ce magasin » est FAUX pour le wallet, deux fois : un porteur n'a pas d'« adresse », et ce qui le
 * rend joignable est une carte qui existe VRAIMENT chez le fournisseur — pas un consentement seul.
 * Une phrase à trou aurait affiché une explication plausible et fausse (§23.0).
 */
const REACHABLE: Record<CampaignChannel, () => string> = {
  email: () => t('Adresse e-mail renseignée, consentement donné à ce magasin, fiche active.'),
  sms: () => t('Numéro mobile renseigné, consentement SMS donné à ce magasin, fiche active.'),
  wallet_push: () => t('Carte wallet active chez le fournisseur, consentement donné à ce magasin, fiche active.'),
};

export const channelReachability = (c: CampaignChannel): string => REACHABLE[c]();

/**
 * COMMENT ON COMPTE LES GENS DE CE CANAL — « joignables » ou « porteurs ».
 * ⚠️ Un porteur de pass n'est pas « joignable » au sens d'une adresse : il l'est parce qu'il porte
 * quelque chose. Le mot de la maquette est le bon, et il se range ici plutôt que dans la table des
 * canaux de l'assistant, où il aurait été le seul à ne pas suivre le type.
 */
const UNIT: Record<CampaignChannel, () => string> = {
  email: () => t('joignables'),
  sms: () => t('joignables'),
  wallet_push: () => t('porteurs'),
};

export const channelUnit = (c: CampaignChannel): string => UNIT[c]();

/**
 * LA MESURE DE RÉCEPTION QUE CE CANAL SAURA RENDRE — le mot de la colonne, le jour où les accusés
 * arriveront.
 *
 * ⚠️ SEUL L'E-MAIL COMPTE DES OUVERTURES : un SMS et une notification wallet se DÉLIVRENT, ils ne
 * s'ouvrent pas. Le ternaire qui vivait ici (« e-mail ? ouvertures : délivrés ») tombait juste pour
 * le wallet — par CHANCE, pas par règle, et la chance ne tient pas au canal suivant.
 */
const METRIC: Record<CampaignChannel, () => string> = {
  email: () => t('ouvertures'),
  sms: () => t('délivrés'),
  wallet_push: () => t('délivrés'),
};

export const channelMetric = (c: CampaignChannel): string => METRIC[c]();
